<template>
  <div class="pcalc">
    <div v-if="loading" class="pcalc-hint">{{ $t("productCalculation.loading") }}</div>
    <div v-else-if="!calc" class="pcalc-hint pcalc-error">{{ $t("productCalculation.loadFailed") }}</div>
    <template v-else>
      <h5 class="pcalc-section">{{ $t("productCalculation.costBuildUp") }}</h5>
      <table class="pcalc-table">
        <tbody>
          <template v-if="calc.purchasePriceEur != null">
            <tr class="pcalc-head calc-row calc-row--a calc-head">
              <calc-block-cell block="a" :rows="blockARows" />
              <td><calc-label :short="$t('calcLabels.ek')" :name="$t('calcLabels.ekName')" :extra="$t('calcLabels.rate', { rate: fmt(calc.exchangeRate, 3) })" :tip="tip('purchaseUsd')" /></td>
              <td class="pcalc-amount">{{ usd(calc.lastPurchasePriceUsd) }}</td>
              <td class="pcalc-text">
                {{ calc.purchaseDate ? $t("productCalculation.purchaseFrom", { date: formatDate(calc.purchaseDate) }) : "" }}
                <last-purchase-refresh @refreshed="load" />
              </td>
            </tr>
            <tr class="calc-row calc-row--a">
              <td><calc-label prefix="=" plain :short="$t('calcLabels.ek')" :name="$t('calcLabels.ekName')" :tip="tip('purchaseEur')" /></td>
              <td class="pcalc-amount">{{ eur(calc.purchasePriceEur) }}</td>
              <td class="pcalc-text"></td>
            </tr>
            <tr class="calc-row calc-row--a">
              <td><calc-label prefix="+" plain :short="$t('calcLabels.bznk')" :name="$t('calcLabels.bznkName')" :tip="tip('bznk')" /></td>
              <td class="pcalc-amount">{{ eur(calc.bznk) }}</td>
              <td class="pcalc-text"></td>
            </tr>
            <tr class="calc-row calc-row--a">
              <td><calc-label prefix="+" :name="$t('calcLabels.customs')" :extra="$t('calcLabels.customsRate', { rate: percent(calc.customsRatePercent, 1) })" :tip="tip('customs')" /></td>
              <td class="pcalc-amount">{{ eur(calc.customs) }}</td>
              <td class="pcalc-text"></td>
            </tr>
          </template>
          <tr class="pcalc-sum calc-row calc-row--a calc-sum">
            <calc-block-cell v-if="calc.purchasePriceEur == null" block="a" :rows="1" />
            <td><calc-label :prefix="calc.purchasePriceEur != null ? '=' : ''" short="BEP" :name="$t('calcLabels.bepName')" :tip="tip('bep')" /></td>
            <td class="pcalc-amount">{{ eur(calc.bep) }}</td>
            <td class="pcalc-text">
              {{ calc.bep == null ? $t("productCalculation.bepMissing") : calc.purchasePriceEur == null ? $t("productCalculation.bepSource") : "" }}
              <last-purchase-refresh v-if="calc.purchasePriceEur == null" @refreshed="load" />
            </td>
          </tr>
          <tr class="calc-row calc-row--b">
            <calc-block-cell block="b" :rows="8" />
            <td><calc-label prefix="+" :name="$t('productCalculation.containerUnloading')" :tip="tip('containerUnloading')" /></td>
            <td class="pcalc-amount">{{ eur(calc.containerUnloadingCost) }}</td>
            <td class="pcalc-text">{{ calc.volumeCbm != null ? $t("productCalculation.volumeHint", { cbm: fmt(calc.volumeCbm, 3) }) : $t("productCalculation.dimensionsMissing") }}</td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('productCalculation.storage')" :tip="tip('storage')" /></td>
            <td class="pcalc-amount">{{ eur(calc.averageStorageCostPerPiece) }}</td>
            <td class="pcalc-text">{{ calc.averageStorageCostPerPiece != null ? "" : $t("productCalculation.noSales") }}</td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('productCalculation.additional')" :tip="tip('additional')" /></td>
            <td class="pcalc-amount">
              <span class="pcalc-cell"><input v-model="form.additionalCost" type="number" min="0" step="0.01" :disabled="!canEdit" :class="{ 'pcalc-invalid': !amountValid(form.additionalCost) }" /><span class="pcalc-unit">€</span></span>
            </td>
            <td class="pcalc-text">
              <input v-model="form.additionalCostNote" type="text" maxlength="500" class="pcalc-note" :disabled="!canEdit" :placeholder="$t('productCalculation.additionalNote')" />
            </td>
          </tr>
          <tr class="pcalc-sum calc-row calc-row--b calc-sum">
            <td><calc-label prefix="=" short="LAP" :name="$t('calcLabels.lapName')" :tip="tip('lap')" /></td>
            <td class="pcalc-amount">{{ eur(lapLocal) }}</td>
            <td class="pcalc-text"></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('productCalculation.pick')" :tip="tip('pick')" /></td>
            <td class="pcalc-amount">{{ eur(calc.pickCost) }}</td>
            <td class="pcalc-text">{{ calc.pickIncludesForwardingSurcharge ? $t("productCalculation.pickForwarding") : "" }}</td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('productCalculation.provision')" :tip="tip('provision')" /></td>
            <td class="pcalc-amount">
              <span class="pcalc-cell"><input v-model="form.provisionCost" type="number" min="0" step="0.01" :disabled="!canEdit" :class="{ 'pcalc-invalid': !amountValid(form.provisionCost) }" /><span class="pcalc-unit">€</span></span>
            </td>
            <td class="pcalc-text"></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('productCalculation.shipping')" :tip="tip('shipping')" /></td>
            <td class="pcalc-amount">{{ eur(calc.shippingCost) }}</td>
            <td class="pcalc-text">{{ shippingHint }}</td>
          </tr>
          <tr class="pcalc-sum calc-row calc-row--b calc-sum">
            <td><calc-label prefix="=" short="BSP" :name="$t('calcLabels.bspName')" :tip="tip('bsp')" /></td>
            <td class="pcalc-amount">{{ eur(bspLocal) }}</td>
            <td class="pcalc-text"></td>
          </tr>
          <tr class="calc-row calc-row--c">
            <calc-block-cell block="c" :rows="2" />
            <td><calc-label prefix="+" :name="$t('productCalculation.fixedShare')" :extra="percent(calc.fixedCostSharePercent)" :tip="tip('fixedShare')" /></td>
            <td class="pcalc-amount">{{ eur(fixedCostLocal) }}</td>
            <td class="pcalc-text">{{ calc.saleNet == null ? $t("productCalculation.noSalePrice") : "" }}</td>
          </tr>
          <tr class="pcalc-sum calc-row calc-row--c calc-sum">
            <td><calc-label prefix="=" short="BKP" :name="$t('calcLabels.bkpName')" :tip="tip('bkp')" /></td>
            <td class="pcalc-amount">{{ eur(bkpLocal) }}</td>
            <td class="pcalc-text"></td>
          </tr>
          <tr class="calc-row calc-row--d">
            <calc-block-cell block="d" :rows="1" />
            <td><calc-label prefix="+" :name="$t('productCalculation.returnCosts')" :extra="$t('calcLabels.returnRate', { rate: percent(calc.returnRatePercent, 1) })" :tip="tip('returnCosts')" /></td>
            <td class="pcalc-amount">{{ eur(calc.returnCost) }}</td>
            <td class="pcalc-text">{{ calc.returnRatePercent != null ? "" : $t("productCalculation.noSales90") }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="canEdit" class="pcalc-actions">
        <md-button class="md-success md-sm" :disabled="saving || !dirty || !formValid" @click="save">{{ $t("productCalculation.save") }}</md-button>
      </div>

      <template v-if="calc.shippingCarriers && calc.shippingCarriers.length">
        <h5 class="pcalc-section">{{ $t("productCalculation.shippingDetails") }}</h5>
        <parcel-carrier-cards :carriers="calc.shippingCarriers" :used-carrier="calc.outboundCarrier" :used-label="$t('productCalculation.usedCarrier')"></parcel-carrier-cards>
      </template>

      <h5 class="pcalc-section">{{ $t("productCalculation.figures") }}</h5>
      <table class="pcalc-table">
        <tbody>
          <tr v-for="f in figures" :key="f.key">
            <td>{{ $t("productCalculation.figure." + f.key) }} <help-tip :text="$t('productCalculation.rule.' + f.key)"></help-tip></td>
            <td class="pcalc-amount">{{ f.value }}</td>
            <td class="pcalc-text"></td>
          </tr>
        </tbody>
      </table>
      <p v-if="calc.returnsDataSince" class="pcalc-hint">{{ $t("productCalculation.returnsSince", { date: formatDate(calc.returnsDataSince) }) }}</p>
    </template>
  </div>
</template>

<script>
import CalcBlockCell from "@/components/Calculation/CalcBlockCell.vue";
import CalcLabel from "@/components/Calculation/CalcLabel.vue";
import HelpTip from "@/components/HelpTip.vue";
import LastPurchaseRefresh from "@/components/Calculation/LastPurchaseRefresh.vue";
import ParcelCarrierCards from "@/components/ParcelCarrierCards.vue";
import { hasPermission } from "../../mixins/auth";
import productCalculationService from "../../services/product-calculation.service";

export default {
  components: { CalcBlockCell, CalcLabel, HelpTip, LastPurchaseRefresh, ParcelCarrierCards },
  props: { productId: { type: Number, required: true } },
  data() {
    return { calc: null, loading: true, saving: false, form: { additionalCost: "", additionalCostNote: "", provisionCost: "" }, original: "" };
  },
  computed: {
    // Block A Wareneingang: mit EK-Zeilen (USD, EUR, BZNK, Zoll) plus BEP, sonst nur BEP
    blockARows() {
      return this.calc && this.calc.purchasePriceEur != null ? 5 : 1;
    },
    canEdit() {
      return hasPermission("Write", "Products", "Edit");
    },
    snapshot() {
      return JSON.stringify([this.form.additionalCost, this.form.additionalCostNote || "", this.form.provisionCost]);
    },
    dirty() {
      return this.snapshot !== this.original;
    },
    formValid() {
      return this.amountValid(this.form.additionalCost) && this.amountValid(this.form.provisionCost);
    },
    additionalValue() {
      return this.form.additionalCost === "" || this.form.additionalCost == null ? 0 : Number(this.form.additionalCost) || 0;
    },
    provisionValue() {
      return this.form.provisionCost === "" || this.form.provisionCost == null ? 0 : Number(this.form.provisionCost) || 0;
    },
    // Zwischensummen live mit den noch nicht gespeicherten Eingaben
    lapLocal() {
      const c = this.calc;
      if (!c || c.bep == null || c.containerUnloadingCost == null || c.averageStorageCostPerPiece == null) return null;
      return c.bep + c.containerUnloadingCost + c.averageStorageCostPerPiece + this.additionalValue;
    },
    bspLocal() {
      const c = this.calc;
      if (this.lapLocal == null || c.shippingCost == null) return null;
      return this.lapLocal + c.pickCost + this.provisionValue + c.shippingCost;
    },
    // Fixkostenanteil in EUR haengt nur am Netto-VK, BKP rechnet mit der live ermittelten BSP
    fixedCostLocal() {
      const c = this.calc;
      if (c.saleNet == null || c.fixedCostSharePercent == null) return null;
      return (c.saleNet * c.fixedCostSharePercent) / 100;
    },
    bkpLocal() {
      if (this.bspLocal == null || this.fixedCostLocal == null) return null;
      return this.bspLocal + this.fixedCostLocal;
    },
    shippingHint() {
      const c = this.calc;
      if (c.shippingCost != null) return this.$t("productCalculation.shippingFrom", { carrier: c.outboundCarrier, peak: this.eur(c.shippingCostPeak) });
      return this.$te("productCalculation.shippingReason." + c.shippingCostUnavailableReason) ? this.$t("productCalculation.shippingReason." + c.shippingCostUnavailableReason) : "";
    },
    figures() {
      const c = this.calc;
      const num = (v, d = 2, unit = "") => (v == null ? "–" : `${this.fmt(v, d)}${unit}`);
      // Einheiten wie bei den Lagerkennzahlen der Uebersicht (Trello 3b9GL2pS, Update H–J); Umschlagshaeufigkeit ohne Einheit
      const pcs = ` ${this.$t("oqsSettings.units.pieces")}`;
      const months = ` ${this.$t("oqsSettings.units.months")}`;
      return [
        { key: "sold12", value: num(c.soldLast12Months, 0, pcs) },
        { key: "sold3", value: num(c.soldLast3Months, 0, pcs) },
        { key: "returns3", value: num(c.returnsLast3Months, 0, pcs) },
        { key: "returnRate", value: num(c.returnRatePercent, 1, " %") },
        { key: "storagePerPiece", value: c.storageCostPerPiece == null ? "–" : this.eur(c.storageCostPerPiece) },
        { key: "averageStock", value: num(c.averageStockPieces, 0, pcs) },
        { key: "storageMonths", value: num(c.averageStorageMonths, 1, months) },
        { key: "storageCost", value: c.averageStorageCostPerPiece == null ? "–" : this.eur(c.averageStorageCostPerPiece) },
        { key: "turnover", value: num(c.stockTurnover, 2) },
        { key: "coverage12", value: num(c.coverageMonths12, 1, months) },
        { key: "coverage3", value: num(c.coverageMonths3, 1, months) },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      return productCalculationService
        .getCalculation(this.productId)
        .then((calc) => this.setCalc(calc))
        .catch(() => {
          this.calc = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setCalc(calc) {
      this.calc = calc;
      this.form = {
        additionalCost: calc.additionalCost == null ? "" : calc.additionalCost,
        additionalCostNote: calc.additionalCostNote || "",
        provisionCost: calc.provisionCost == null ? "" : calc.provisionCost,
      };
      this.$nextTick(() => {
        this.original = this.snapshot;
      });
    },
    amountValid(v) {
      return v === "" || v == null || (Number.isFinite(Number(v)) && Number(v) >= 0);
    },
    fmt(value, decimals = 2) {
      return this.$options.filters.formatNumberGerman(value, decimals);
    },
    eur(value, decimals = 2) {
      return value == null ? "–" : `${this.fmt(value, decimals)} €`;
    },
    percent(value, decimals = 2) {
      return value == null ? "–" : `${this.fmt(value, decimals)} %`;
    },
    usd(value) {
      return value == null ? "–" : `${this.fmt(value)} USD`;
    },
    tip(key) {
      return this.$t("productCalculation.tip." + key);
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString(this.$i18n.locale) : "";
    },
    save() {
      this.saving = true;
      const toNumber = (v) => (v === "" || v == null ? null : Number(v));
      return productCalculationService
        .saveCalculation(this.productId, {
          additionalCost: toNumber(this.form.additionalCost),
          additionalCostNote: this.form.additionalCostNote || null,
          provisionCost: toNumber(this.form.provisionCost),
        })
        .then((calc) => {
          this.setCalc(calc);
          this.$notify({ timeout: 2500, message: this.$t("productCalculation.saved"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "success" });
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 403) return;
          this.$notify({ timeout: 2500, message: this.$t("productCalculation.saveFailed"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "danger" });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.pcalc {
  text-align: left;
}
.pcalc-section {
  margin: 18px 0 8px;
  font-weight: 500;
}
.pcalc-hint {
  color: #777;
  font-size: 12px;
  margin: 8px 0;
}
.pcalc-error {
  color: #f44336;
}
.pcalc-table {
  width: 100%;
  border-collapse: collapse;
}
.pcalc-table td {
  padding: 5px 6px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.pcalc-amount {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  width: 150px;
}
.pcalc-text {
  color: #666;
  font-size: 12px;
}
.pcalc-sum td {
  border-top: 2px solid #ddd;
}
.pcalc-sum .pcalc-amount,
.pcalc-head .pcalc-amount {
  font-weight: 700;
}
.pcalc-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.pcalc-cell input {
  width: 90px;
  padding: 3px 5px;
  border: 1px solid #ddd;
  border-radius: 3px;
  text-align: right;
}
.pcalc-note {
  width: 100%;
  padding: 3px 5px;
  border: 1px solid #ddd;
  border-radius: 3px;
}
.pcalc-cell input:disabled,
.pcalc-note:disabled {
  background: #f6f6f6;
}
.pcalc-invalid {
  border-color: #f44336 !important;
}
.pcalc-unit {
  color: #777;
  font-size: 12px;
}
.pcalc-actions {
  text-align: right;
}
</style>
